import moment from 'moment';

import {
  BasketHotelViewModel,
  BasketTourViewModel,
  BasketFlightViewModel,
  HotelPrebookUpdateGuestsRequestViewModel,
  TourPrebookUpdateGuestsRequestViewModel,
  FlightPrebookUpdateTravelerRequestViewModel,
  BasketLoyaltyKocAilemViewModel,
  UserViewModel,
  BasketViewModel,
  MyUserGetViewModel,
  TourPrebookUpdatePersonRequestViewModel,
  BasketExtraServicesViewModel,
  ProviderType,
} from '@api/v3';
import { HotelPrebookUpdatePersonRequestViewModel } from '@api/index';
import { getBasketFromCache } from '@hooks/cache';
import { formatISOToDate } from '@utils/dateUtil';
import { generateUUID } from '@utils/generateUUID';

import { FormState } from './contexts/hotel/types';
import { GuestInformations } from './contexts/common/types';
import { isValidGuestForSave } from './info-forms/common/SavedGuests';
import {
  TOUR_FIELDS_TO_COMPARE,
  compareSavedUsersToTourGuests,
  formatTourItemBeforeComparison,
} from './info-forms/tour/helpers';
import {
  HOTEL_FIELDS_TO_COMPARE,
  compareSavedUsersToHotelGuests,
  formatHotelItemBeforeComparison,
} from './info-forms/hotel/helpers';
import { formatSavedContactsFields } from './info-forms/helpers';
import {
  Flight_FIELDS_TO_COMPARE,
  compareSavedUsersToFlightGuests,
  formatFlightItemBeforeComparison,
} from './info-forms/flight/utils';
import {
  checkValidateAdultAge,
  checkValidateChildAge,
  checkValidateInfantAge,
} from './validation/flight';
import { PrebookExtraServiceItem } from './contexts/extra-services/types';

const hasSingleHotelWithMultipleRooms = (
  hotels?: Array<BasketHotelViewModel>,
  tours?: Array<BasketTourViewModel>
) =>
  hotels?.every((hotel) => hotel?.id === hotels?.[0]?.id) ||
  tours?.every((tour) => tour?.id === tours?.[0]?.id);

export const validSeturBasketItems = (
  tours: Array<BasketTourViewModel>,
  flights: Array<BasketFlightViewModel>,
  hotels: Array<BasketHotelViewModel>,
  isSeturEmployee: boolean
) => {
  // check if the basket has one domestic
  const oneDomesticHotel =
    hotels.length > 0 ? hotels?.find((hotel) => hotel?.country === 'TR') : null;

  const validHotelRooms = hasSingleHotelWithMultipleRooms(hotels, tours);

  // if the basket has a domestic hotel and tours , should return not valid
  if (isSeturEmployee && oneDomesticHotel && tours?.length > 0) return false;
  // if the basket has a domestic hotel and flights , should return not valid
  if (isSeturEmployee && oneDomesticHotel && flights?.length > 0) return false;

  // if the basket has one hotel with more than 1 rooms, it should be valid
  if (isSeturEmployee && validHotelRooms) return true;

  // if the basket has a domestic hotel and none-domestic hotels , should return not valid
  if (isSeturEmployee && oneDomesticHotel && hotels?.length > 1) return false;

  if (isSeturEmployee && hotels?.[0]?.country === 'TR') return true;

  // if the basket has any other items , only tours, only flights or both, should return valid
  return true;
};

export const hasOneDomesticHotel = (
  tours: Array<BasketTourViewModel>,
  flights: Array<BasketFlightViewModel>,
  hotels: Array<BasketHotelViewModel>,
  isSeturEmployee: boolean
) => {
  const hasValidHotelTourRooms = hasSingleHotelWithMultipleRooms(hotels, tours);
  const hotel = hotels?.[0]?.country === 'TR' || hotels?.[0]?.country === 'CY';

  if (
    hotels.length > 0 &&
    hotels?.some((i) => i?.provider !== ProviderType.SETUR_LOCAL_HOTEL)
  )
    return false;
  if (isSeturEmployee && !hasValidHotelTourRooms) return false;

  if (isSeturEmployee && tours?.length > 0 && flights?.length === 0)
    return true;
  if (isSeturEmployee && hotel && flights?.length > 0) return false;
  if (isSeturEmployee && hotel && flights?.length === 0 && tours?.length === 0)
    return true;
};

export const hasMultipleDomesticHotels = (
  hotels: Array<BasketHotelViewModel>,
  tours: Array<BasketTourViewModel>,
  isSeturEmployee: boolean
) => {
  // check if the basket has more than one domestic hotel
  const domesticHotels = [];
  const hasValidHotelTourRooms = hasSingleHotelWithMultipleRooms(hotels, tours);

  if (hotels?.length === 0 || tours.length === 0) return false;
  for (const hotel of hotels) {
    if (hotel?.country === 'TR') {
      domesticHotels.push(hotel);
    }
  }
  for (const tour of tours) {
    if (tour) {
      domesticHotels.push(tour);
    }
  }
  if (isSeturEmployee && hasValidHotelTourRooms) return false;
  if (isSeturEmployee && domesticHotels?.length > 1) return true;

  return false;
};

export const hasDomesticHotel = (hotels: Array<BasketHotelViewModel>) => {
  if (hotels?.length === 0) return false;
  for (const hotel of hotels) {
    if (hotel?.country === 'TR') return true;
  }

  return false;
};

export const convertDataHotel = (
  guests: HotelPrebookUpdateGuestsRequestViewModel[] = [],
  itemId?: string,
  registeredGuests?: MyUserGetViewModel[]
) => {
  if (!guests?.length) return [];

  const allCurrentGuests = guests.reduce(
    (acc: HotelPrebookUpdatePersonRequestViewModel[], item) => {
      return [...acc, ...item.adults, ...item.childs];
    },
    []
  );

  const alreadyRegisteredGuestItems: HotelPrebookUpdatePersonRequestViewModel[] =
    [];
  const newGuestsItems: GuestInformations[] = [];
  const formattedSavedContacts = formatSavedContactsFields(registeredGuests);

  allCurrentGuests.forEach((item) => {
    if (formattedSavedContacts?.length) {
      if (
        isValidGuestForSave(item) &&
        !compareSavedUsersToHotelGuests(
          formattedSavedContacts,
          formatHotelItemBeforeComparison(item),
          HOTEL_FIELDS_TO_COMPARE
        )
      ) {
        alreadyRegisteredGuestItems.push(item);
      }
    } else {
      if (isValidGuestForSave(item)) {
        alreadyRegisteredGuestItems.push(item);
      }
    }
  });

  const inputFormat = 'DD/MM/YYYY';

  alreadyRegisteredGuestItems.forEach((guest) => {
    newGuestsItems.push({
      id: generateUUID(),
      firstname: guest.firstname?.trim(),
      lastname: guest.lastname?.trim(),
      gender: guest.gender,
      hesCode: guest.hesCode,
      birthdate: moment(guest?.birthdate, inputFormat).format(),
      itemId,
    });
  });

  let currentData = [];
  if (typeof window !== 'undefined') {
    currentData = JSON.parse(localStorage.getItem('guests') ?? '[]');
  }

  return [...currentData, ...newGuestsItems];
};
export const convertDataTour = (
  body: TourPrebookUpdateGuestsRequestViewModel[],
  itemId: string,
  registeredGuests?: MyUserGetViewModel[]
) => {
  if (!body?.length) return [];

  const allCurrentGuests = body.reduce(
    (acc, item) => [...acc, ...item.adults, ...item.childs],
    []
  );

  const alreadyRegisteredGuestItems: TourPrebookUpdatePersonRequestViewModel[] =
    [];
  const newGuestsItems: GuestInformations[] = [];
  const formattedSavedContacts = formatSavedContactsFields(registeredGuests);

  allCurrentGuests.forEach((item) => {
    if (formattedSavedContacts?.length) {
      if (
        isValidGuestForSave(item) &&
        !compareSavedUsersToTourGuests(
          formattedSavedContacts,
          formatTourItemBeforeComparison(item),
          TOUR_FIELDS_TO_COMPARE
        )
      ) {
        alreadyRegisteredGuestItems.push(item);
      }
    } else {
      if (isValidGuestForSave(item)) {
        alreadyRegisteredGuestItems.push(item);
      }
    }
  });

  const inputFormat = 'DD/MM/YYYY';

  alreadyRegisteredGuestItems.forEach((guest) =>
    newGuestsItems.push({
      id: generateUUID(),
      firstname: guest.firstname?.trim(),
      lastname: guest.lastname?.trim(),
      gender: guest.gender,
      hesCode: guest.hesCode,
      birthdate: moment(guest?.birthdate, inputFormat).format(),
      itemId,
      trin: guest?.identityNumber,
      phone: guest?.phoneNumber,
    })
  );

  let currentData = [];
  if (typeof window !== 'undefined') {
    currentData = JSON.parse(localStorage.getItem('guests') ?? '[]');
  }

  return [...currentData, ...newGuestsItems];
};
export const convertDataFlight = (
  body: FlightPrebookUpdateTravelerRequestViewModel[] = [],
  itemId?: string,
  registeredGuests?: MyUserGetViewModel[]
) => {
  const alreadyRegisteredGuestItems: FlightPrebookUpdateTravelerRequestViewModel[] =
    [];
  const newGuestsItems: GuestInformations[] = [];
  const formattedSavedContacts = formatSavedContactsFields(registeredGuests);

  body?.forEach((item) => {
    if (formattedSavedContacts?.length) {
      if (
        isValidGuestForSave({
          firstname: item?.firstName,
          lastname: item?.lastName,
          birthdate: item?.birthDate,
          gender: item?.gender,
        }) &&
        !compareSavedUsersToFlightGuests(
          formattedSavedContacts,
          formatFlightItemBeforeComparison(item),
          Flight_FIELDS_TO_COMPARE
        )
      ) {
        alreadyRegisteredGuestItems.push(item);
      }
    } else {
      if (
        isValidGuestForSave({
          firstname: item?.firstName,
          lastname: item?.lastName,
          birthdate: item?.birthDate,
          gender: item?.gender,
        })
      ) {
        alreadyRegisteredGuestItems.push(item);
      }
    }
  });

  const inputFormat = 'DD/MM/YYYY';

  alreadyRegisteredGuestItems.forEach((guest) =>
    newGuestsItems.push({
      id: generateUUID(),
      firstname: guest.firstName?.trim(),
      lastname: guest.lastName?.trim(),
      gender: guest.gender,
      hesCode: guest.hesCode,
      birthdate: moment(guest?.birthDate, inputFormat).format(),
      itemId,
      trin: guest?.identityNumber,
      phone: guest?.phoneNumber,
    })
  );

  let currentData = [];
  if (typeof window !== 'undefined') {
    currentData = JSON.parse(localStorage.getItem('guests') ?? '[]');
  }

  return [...currentData, ...newGuestsItems];
};

//After flight variations completed we need to add BasketFlightViewModel to here.
export const getSeturLoyaltySelectedPromotion = (
  items: BasketHotelViewModel[] | BasketTourViewModel[]
) => {
  for (const item of items) {
    if (item?.variations && item?.variations?.length === 0) {
      return undefined;
    }
    const selectedVariation = item?.variations?.find(
      (variation) => variation?.selected
    );
    return selectedVariation?.promotions?.find(
      (promotion) =>
        promotion?.loyaltyType && promotion?.loyaltyType === 'SeturPointEarn'
    );
  }
};
export const isUserInHotelGuests = (body: FormState, user?: UserViewModel) => {
  if (!user || !body || !body.items) return false;
  if (!user?.isSeturEmployee) return false;

  let isValidUser = false;
  for (const item of body.items) {
    for (const adult of item.guest.adults) {
      if (
        adult.firstname?.trim()?.toLocaleLowerCase('tr-TR') ===
          user.firstName?.trim()?.toLocaleLowerCase('tr-TR') &&
        adult.lastname?.trim()?.toLocaleLowerCase('tr-TR') ===
          user.lastName?.trim()?.toLocaleLowerCase('tr-TR')
      )
        return (isValidUser = true);
    }
  }

  return isValidUser;
};

export const isKocAilemUserInGuests = (
  body,
  user?: BasketLoyaltyKocAilemViewModel
) => {
  if (!user || !body) return false;

  let isValidUser = false;
  for (const item of body) {
    for (const adult of item.guest.adults) {
      if (
        adult.firstname?.trim()?.toLocaleLowerCase('tr-TR') ===
          user.name?.trim()?.toLocaleLowerCase('tr-TR') &&
        adult.lastname?.trim()?.toLocaleLowerCase('tr-TR') ===
          user.surname?.trim()?.toLocaleLowerCase('tr-TR')
      )
        return (isValidUser = true);
    }
  }
  return isValidUser;
};

export const isEmptyBasket = (basket?: BasketViewModel) => {
  if (!getBasketFromCache()?.id) {
    return true;
  }
  return (
    basket?.flights?.length === 0 &&
    basket?.hotels?.length === 0 &&
    basket?.tours?.length === 0
  );
};

// Searches for a "guest user" object within an array based on matching fields.
export function findAGuestUserFromFields(
  arr: any[],
  targetObject: any,
  fieldsToCheck: string[]
): any | undefined {
  for (const obj of arr) {
    let isMatch = true;

    for (const field of fieldsToCheck) {
      if (
        field in obj &&
        String(obj[field]).toLowerCase() !==
          String(targetObject[field]).toLowerCase()
      ) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      return obj;
    }
  }

  return undefined;
}

export const handleTravelersBirthDatesErrors = (attributeName: string) => {
  const elements = Array.from(
    document?.querySelectorAll(`[data-${attributeName}=true]`)
  );
  const firstElement = elements?.[0];

  if (!firstElement) return;
  const elementPosition = firstElement.getBoundingClientRect().top;

  window.scrollTo({
    top: elementPosition + window.scrollY, // Account for current scroll position
    behavior: 'smooth', // Optional for smooth scrolling
  });
};

const flightTravelersValidator = (
  birthDate: string,
  type: 'INF' | 'CHD' | 'ADT' = 'INF',
  flights: BasketFlightViewModel[]
): boolean => {
  const flightDates = flights?.[0]?.plan?.flights?.map(
    (it) => it.departureDate
  );

  const formattedFlightDates = flightDates?.map((it) => formatISOToDate(it));
  const formattedFlightDate = moment(
    formattedFlightDates?.[formattedFlightDates?.length - 1],
    'DD/MM/YYYY'
  );
  const formattedBirthDate = moment(birthDate, 'DD/MM/YYYY') || '';
  const age = moment(formattedFlightDate).diff(formattedBirthDate, 'years');

  return type === 'INF'
    ? checkValidateInfantAge(age)
    : type === 'ADT'
    ? checkValidateAdultAge(age)
    : checkValidateChildAge(age);
};

export const validateFlightTravelersBirthDates = (
  flightBody: any,
  basketFlights: BasketFlightViewModel[]
) => {
  if (!basketFlights?.length) return true;
  return flightBody?.travelers?.every((traveler) => {
    return flightTravelersValidator(
      traveler.birthDate,
      traveler.passengerTypeCode as 'INF' | 'CHD' | 'ADT',
      basketFlights
    );
  });
};

// ========================================
// Check extra services sets count
// ========================================
export const checkExtraServicesSeatsCount = (
  prebookExtraServices?: PrebookExtraServiceItem[],
  basketExtraServices?: BasketExtraServicesViewModel[]
) => {
  if (!prebookExtraServices?.length) return true;

  return basketExtraServices?.every((extraService) => {
    const prebookExtraService = prebookExtraServices.find(
      (item) =>
        item.id === extraService.id &&
        item.parentItemId === extraService.parentItemId
    );

    return (
      prebookExtraService?.guestsId?.length === extraService.numberOfPeople
    );
  });
};

// ========================================
// Check extra services sets count
// ========================================
export const checkExtraServicesSeatsCountHotel = (
  prebookExtraServices?: PrebookExtraServiceItem[],
  basketExtraServices?: BasketExtraServicesViewModel[]
) => {
  if (!prebookExtraServices?.length) return true;

  return basketExtraServices?.every((extraService) => {
    const prebookExtraService = prebookExtraServices.find(
      (item) =>
        item.id === extraService.id &&
        item.parentItemId === extraService.parentItemId
    );

    return (
      prebookExtraService?.guestsId?.length === extraService.numberOfPeople
    );
  });
};
